import React, { useState, useContext, useMemo } from 'react';
import { sessionLabel } from '../../../utils/leaveSession';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    Button,
    TextField,
    InputAdornment,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stack,
    Divider,
} from '@mui/material';
import {
    Search as SearchIcon,
    Event as EventIcon,
    Info as InfoIcon,
    Visibility as VisibilityIcon,
    EventNote as TotalIcon,
    HourglassEmpty as PendingIcon,
    CheckCircle as ApprovedIcon,
    Cancel as DeclinedIcon,
} from '@mui/icons-material';
import { useLeaveRequestsforme, useUpdateEmpLeaveRequest } from '../../../hooks/useLeaves';
import { AuthContext } from '../../../context/logeduser';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX, AVATAR_SX } from '../../common/tableStyles';
import PageHeader from '../PageHeader';

// ---- Visual tokens (shared with the employee Home, Attendance & Payslip dashboards) ----
const CARD_SX = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: '22px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue

const TeamLeaves = () => {
    const { authData } = useContext(AuthContext);
    const id = authData?.employee_no;
    const { data: teamRequests, isLoading, error: leaveError } = useLeaveRequestsforme(id);
    const { mutate: updateStatus } = useUpdateEmpLeaveRequest();

    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState(''); // 'APPROVE' or 'DECLINE'
    const [statusReason, setStatusReason] = useState('');

    const statusColors = {
        PENDING: { bg: '#fffbeb', color: '#f59e0b', border: '#f59e0b33' },
        ACCEPTED: { bg: '#f0fdf4', color: '#16a34a', border: '#16a34a33' },
        DECLINED: { bg: '#fef2f2', color: '#ef4444', border: '#ef444433' },
    };

    const stats = useMemo(() => {
        if (!teamRequests) return { total: 0, pending: 0, approved: 0, declined: 0 };
        return {
            total: teamRequests.length,
            pending: teamRequests.filter(r => r.status === 'PENDING').length,
            approved: teamRequests.filter(r => r.status === 'ACCEPTED').length,
            declined: teamRequests.filter(r => r.status === 'DECLINED').length,
        };
    }, [teamRequests]);

    const filteredRequests = useMemo(() => {
        if (!teamRequests) return [];
        return teamRequests.filter(r => {
            const matchesSearch =
                r.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
                String(r.employee_no).includes(search);
            const matchesStatus = filterStatus === 'ALL' || r.status === filterStatus;
            return matchesSearch && matchesStatus;
        });
    }, [teamRequests, search, filterStatus]);

    const handleAction = (request, type) => {
        setSelectedRequest(request);
        setActionType(type);
        setStatusReason('');
        setActionDialogOpen(true);
    };

    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setDetailsDialogOpen(true);
    };

    const confirmAction = () => {
        if (!statusReason && actionType === 'DECLINE') {
            alert('Please provide a reason for declining.');
            return;
        }
        updateStatus({
            leave_request_id: selectedRequest.id,
            status: actionType === 'APPROVE' ? 'ACCEPTED' : 'DECLINED',
            status_reason: statusReason
        });
        setActionDialogOpen(false);
        setDetailsDialogOpen(false);
    };

    const StatusChip = ({ status }) => {
        const styles = statusColors[status] || { bg: '#eef4ff', color: ACCENT, border: `${ACCENT}33` };
        return (
            <Chip
                label={status}
                size="small"
                sx={{
                    backgroundColor: styles.bg,
                    color: styles.color,
                    border: `1px solid ${styles.border}`,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: '24px',
                    borderRadius: '8px'
                }}
            />
        );
    };

    if (leaveError?.response?.status === 403) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 } }}>
                <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 6 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <InfoIcon />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#ef4444' }}>Access Denied</Typography>
                    <Typography variant="body1" sx={{ color: '#6b7280' }}>This page is only accessible to Managers and Team Leads.</Typography>
                </Paper>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ pb: { xs: 4, md: 2 } }}>
            <PageHeader
                icon={<EventIcon />}
                title="Team Leave Management"
                subtitle="Review and approve leave requests from your team members."
            />

            {/* Stats / filter pills card */}
            <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
                <Grid container spacing={2}>
                    {[
                        { label: 'Total Requests', value: stats.total, color: ACCENT, tint: '#eef4ff', status: 'ALL', icon: <TotalIcon /> },
                        { label: 'Pending Approval', value: stats.pending, color: '#f59e0b', tint: '#fffbeb', status: 'PENDING', icon: <PendingIcon /> },
                        { label: 'Approved', value: stats.approved, color: '#16a34a', tint: '#f0fdf4', status: 'ACCEPTED', icon: <ApprovedIcon /> },
                        { label: 'Declined', value: stats.declined, color: '#ef4444', tint: '#fef2f2', status: 'DECLINED', icon: <DeclinedIcon /> },
                    ].map((stat, index) => {
                        const active = filterStatus === stat.status;
                        return (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card
                                    elevation={0}
                                    onClick={() => setFilterStatus(stat.status)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: '16px',
                                        transition: 'all 0.2s',
                                        bgcolor: active ? stat.tint : '#fff',
                                        border: `1px solid ${active ? `${stat.color}33` : '#eef0f4'}`,
                                        '&:hover': { boxShadow: '0 8px 22px rgba(17,24,39,0.07)', transform: 'translateY(-2px)' }
                                    }}
                                >
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: `${stat.color}14`, color: stat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            {React.cloneElement(stat.icon, { sx: { fontSize: 20 } })}
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="caption" fontWeight={700} sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.62rem' }}>
                                                {stat.label}
                                            </Typography>
                                            <Typography sx={{ fontWeight: 800, color: stat.color, lineHeight: 1.1, fontSize: '1.5rem' }}>
                                                {stat.value}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Actions Bar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Search team members..."
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        }}
                        sx={{ width: { xs: '100%', sm: 320 }, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}
                    />
                </Box>
            </Paper>

            {/* Table card */}
            <Paper elevation={0} sx={CARD_SX}>
                <TableContainer component={Paper} elevation={0} sx={{ ...TABLE_PAPER_SX, boxShadow: 'none', border: 'none' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={HEAD_CELL_SX}>EMPLOYEE</TableCell>
                                <TableCell sx={HEAD_CELL_SX}>LEAVE TYPE</TableCell>
                                <TableCell sx={HEAD_CELL_SX}>DURATION</TableCell>
                                <TableCell sx={HEAD_CELL_SX}>STATUS</TableCell>
                                <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>ACTIONS</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRequests.length > 0 ? filteredRequests.map((row) => (
                                <TableRow
                                    key={row.id}
                                    hover
                                    onClick={() => handleViewDetails(row)}
                                    sx={{ ...TABLE_ROW_SX, cursor: 'pointer' }}
                                >
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={AVATAR_SX}>
                                                {row.employee_name?.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{row.employee_name}</Typography>
                                                <Typography variant="caption" color="textSecondary">ID: {row.employee_no}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Typography variant="body2">{row.leave_type}</Typography>
                                    </TableCell>
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Typography variant="body2">{row.start_date} → {row.end_date}</Typography>
                                    </TableCell>
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <StatusChip status={row.status} />
                                            {row.is_backdated && (
                                                <Chip
                                                    label="Back-dated"
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 700, borderRadius: '6px' }}
                                                />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={BODY_CELL_SX} onClick={(e) => e.stopPropagation()}>
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <IconButton size="small" onClick={() => handleViewDetails(row)} sx={{ color: ACCENT, bgcolor: `${ACCENT}14` }}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                            {row.status === 'PENDING' && (
                                                <>
                                                    <Button
                                                        variant="outlined" color="success" size="small"
                                                        onClick={() => handleAction(row, 'APPROVE')}
                                                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        variant="outlined" color="error" size="small"
                                                        onClick={() => handleAction(row, 'DECLINE')}
                                                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                                                    >
                                                        Decline
                                                    </Button>
                                                </>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 8, border: 'none' }}>
                                        <Stack alignItems="center" spacing={1.5}>
                                            <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <EventIcon />
                                            </Box>
                                            <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No team leave requests found.</Typography>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Details Dialog */}
            <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Leave Details
                    {selectedRequest && <StatusChip status={selectedRequest.status} />}
                </DialogTitle>
                <DialogContent dividers>
                    {selectedRequest && (
                        <Stack spacing={2.5} sx={{ py: 1 }}>
                            {selectedRequest.is_backdated && (
                                <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#fffbeb', border: '1px solid #f59e0b33' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#f59e0b' }}>
                                        ⚠ BACK-DATED / REGULARIZATION
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                        This request was submitted after the leave start date.
                                    </Typography>
                                </Box>
                            )}
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>EMPLOYEE</Typography>
                                    <Typography variant="body1" fontWeight={600}>{selectedRequest.employee_name}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>LEAVE TYPE</Typography>
                                    <Typography variant="body1" fontWeight={600}>{selectedRequest.leave_type}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PERIOD</Typography>
                                    <Typography variant="body2">{selectedRequest.start_date} to {selectedRequest.end_date}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>SESSION</Typography>
                                    <Typography variant="body2">{sessionLabel(selectedRequest.session_from, selectedRequest.session_to)}</Typography>
                                </Grid>
                            </Grid>

                            <Divider />

                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>REASON</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #eef0f4' }}>
                                    {selectedRequest.reason || "No reason specified."}
                                </Typography>
                            </Box>

                            {selectedRequest.status !== 'PENDING' && (
                                <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#eef4ff', border: `1px solid ${ACCENT}22` }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: ACCENT }}>MANAGER'S NOTE</Typography>
                                    <Typography variant="body2">{selectedRequest.status_reason || "No remarks provided."}</Typography>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDetailsDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}>Close</Button>
                    {selectedRequest?.status === 'PENDING' && (
                        <>
                            <Button variant="contained" color="error" disableElevation onClick={() => handleAction(selectedRequest, 'DECLINE')} sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 700 }}>Decline</Button>
                            <Button variant="contained" color="success" disableElevation onClick={() => handleAction(selectedRequest, 'APPROVE')} sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 700 }}>Approve</Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Action Confirmation Dialog */}
            <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>{actionType === 'APPROVE' ? 'Confirm Approval' : 'Provide Reason'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        {actionType === 'APPROVE' ? 'Add optional remarks for the employee.' : 'Please explain why this request is being declined.'}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Enter your comments here..."
                        value={statusReason}
                        onChange={(e) => setStatusReason(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setActionDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={actionType === 'APPROVE' ? 'success' : 'error'}
                        disableElevation
                        onClick={confirmAction}
                        sx={{ borderRadius: '14px', px: 3, textTransform: 'none', fontWeight: 700 }}
                    >
                        Confirm {actionType === 'APPROVE' ? 'Approval' : 'Decline'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default TeamLeaves;
